import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';
import { formatCurrency } from '@outlaw-oasis/utils';

interface RedeemKey {
  redeemToken: string;
  redeemed: boolean;
}

interface PurchasedItem {
  id: string;
  orderId: string;
  orderNumber: string;
  productId: string;
  productName: string;
  productPrice: number;
  quantity: number;
  subtotal: number;
  thumbnailUrl?: string;
  keys: RedeemKey[];
  deliveryStatus: string;
  deliveredAt?: string;
  purchasedAt: string;
  seller: {
    id: string;
    storeName: string;
    storeSlug: string;
  };
}

interface DiscordStatus {
  linked: boolean;
  discordUsername: string | null;
  discordAvatar: string | null;
}

export default function Purchases() {
  const { data, isLoading } = useQuery({
    queryKey: ['purchases'],
    queryFn: () => api.get('/orders/purchases'),
  });

  const { data: discordData, isLoading: discordLoading } = useQuery({
    queryKey: ['discord-status'],
    queryFn: () => api.get('/auth/discord/status'),
  });

  const purchasedItems: PurchasedItem[] = (data as any)?.data?.purchases || [];
  const discord: DiscordStatus = (discordData as any)?.data || { linked: false, discordUsername: null, discordAvatar: null };

  const copyToClipboard = (text: string, label = 'Copied!') => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const handleLinkDiscord = async () => {
    try {
      const res = await api.get('/auth/discord') as any;
      window.location.href = res.data.url;
    } catch {
      toast.error('Failed to start Discord authorization.');
    }
  };

  if (isLoading) {
    return (
      <div>
        <h1 className="font-western text-3xl text-cream mb-8">My Purchases</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="flex gap-4">
                <div className="w-20 h-20 bg-leather rounded" />
                <div className="flex-1">
                  <div className="h-5 bg-leather rounded w-3/4 mb-2" />
                  <div className="h-4 bg-leather rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-western text-3xl text-cream mb-6">My Purchases</h1>

      {/* Discord link banner */}
      {!discordLoading && (
        <div className={`rounded-lg p-4 mb-6 flex items-center justify-between gap-4 ${
          discord.linked
            ? 'bg-green-900/20 border border-green-500/30'
            : 'bg-gold/10 border border-gold/30'
        }`}>
          <div className="flex items-center gap-3">
            {discord.linked && discord.discordAvatar && (
              <img src={discord.discordAvatar} alt="" className="w-9 h-9 rounded-full border border-gold/30" />
            )}
            <div>
              {discord.linked ? (
                <>
                  <p className="text-cream text-sm font-semibold">Discord linked — {discord.discordUsername}</p>
                  <p className="text-text-secondary text-xs">
                    Use <code className="bg-leather px-1 rounded">/redeem &lt;token&gt;</code> in our Discord to reveal your keys.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-cream text-sm font-semibold">Link your Discord to redeem keys</p>
                  <p className="text-text-secondary text-xs">
                    You must authorize with Discord before you can redeem keys on the bot.
                  </p>
                </>
              )}
            </div>
          </div>
          {discord.linked ? (
            <a
              href="https://discord.gg/u8Tuv7ns5r"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex-shrink-0 text-sm py-2 px-4"
            >
              Open Discord
            </a>
          ) : (
            <button onClick={handleLinkDiscord} className="btn-primary flex-shrink-0 text-sm py-2 px-4">
              Link Discord
            </button>
          )}
        </div>
      )}

      {purchasedItems.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-text-secondary mb-4">You haven't purchased anything yet.</p>
          <Link to="/products" className="btn-primary">
            Browse Products
          </Link>
        </div>
      ) : (
        <>
          <div className="mb-4 text-text-secondary text-sm">
            {purchasedItems.length} item{purchasedItems.length !== 1 ? 's' : ''} purchased
          </div>

          <div className="space-y-4">
            {purchasedItems.map((item) => (
              <div key={item.id} className="card p-6">
                <div className="flex gap-4 mb-4">
                  {/* Thumbnail */}
                  <div className="w-20 h-20 bg-leather rounded overflow-hidden flex-shrink-0">
                    {item.thumbnailUrl ? (
                      <img src={item.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-gold/30 font-western">OO</span>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/products/${item.productId}`}
                      className="text-cream hover:text-gold transition-colors font-semibold block truncate"
                    >
                      {item.productName}
                    </Link>
                    <div className="text-text-secondary text-sm mt-1">
                      Qty: {item.quantity} &times; {formatCurrency(item.productPrice)}
                    </div>
                    <div className="text-gold font-semibold mt-1">
                      {formatCurrency(item.subtotal)}
                    </div>
                  </div>

                  {/* Order & Seller Info */}
                  <div className="text-right text-sm flex-shrink-0">
                    <Link
                      to={`/dashboard/orders/${item.orderId}`}
                      className="text-gold hover:text-gold-bright"
                    >
                      #{item.orderNumber}
                    </Link>
                    <div className="text-text-muted mt-1">
                      {new Date(item.purchasedAt).toLocaleDateString()}
                    </div>
                    <Link
                      to={`/sellers/${item.seller.storeSlug}`}
                      className="text-text-secondary hover:text-cream block mt-1"
                    >
                      {item.seller.storeName}
                    </Link>
                  </div>
                </div>

                {/* Delivery & Redeem Tokens */}
                <div className="border-t border-gold/10 pt-4">
                  {item.deliveryStatus === 'delivered' ? (
                    <div className="text-success text-sm mb-3">
                      Delivered{item.deliveredAt && ` on ${new Date(item.deliveredAt).toLocaleDateString()}`}
                    </div>
                  ) : (
                    <div className="text-gold text-sm mb-3">Delivery pending</div>
                  )}

                  {item.keys && item.keys.length > 0 ? (
                    <div className="bg-background rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-text-muted text-sm">
                          {item.keys.length > 1 ? `${item.keys.length} Redeem Tokens` : 'Redeem Token'}
                        </span>
                        <span className="text-text-muted text-xs">
                          Run <code className="bg-leather px-1 rounded">/redeem &lt;token&gt;</code> in Discord
                        </span>
                      </div>

                      <div className="space-y-2">
                        {item.keys.map((k, index) => (
                          <div
                            key={index}
                            className={`flex items-center justify-between rounded px-3 py-2 group ${
                              k.redeemed ? 'bg-leather/50 opacity-60' : 'bg-leather'
                            }`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <code className="text-gold font-mono text-sm tracking-widest">{k.redeemToken}</code>
                              {k.redeemed && (
                                <span className="text-xs text-text-muted bg-background px-1.5 py-0.5 rounded flex-shrink-0">
                                  redeemed
                                </span>
                              )}
                            </div>
                            {!k.redeemed && (
                              <button
                                onClick={() => copyToClipboard(k.redeemToken, 'Token copied!')}
                                className="text-text-muted hover:text-gold ml-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                                title="Copy token"
                              >
                                Copy
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      {!discord.linked && (
                        <div className="mt-3 flex items-center justify-between bg-gold/5 border border-gold/20 rounded px-3 py-2">
                          <p className="text-text-secondary text-xs">Link Discord first to redeem these tokens</p>
                          <button
                            onClick={handleLinkDiscord}
                            className="text-gold hover:text-gold-bright text-xs font-semibold ml-3 flex-shrink-0"
                          >
                            Link Discord →
                          </button>
                        </div>
                      )}
                    </div>
                  ) : item.deliveryStatus === 'delivered' ? (
                    <div className="text-text-secondary text-sm">
                      Check your{' '}
                      <Link to={`/dashboard/orders/${item.orderId}`} className="text-gold hover:text-gold-bright">
                        order
                      </Link>{' '}
                      for download links.
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
